import {AABB} from "../core/AABB.js";
import {Vec2} from "../core/Vec2.js";
import GameObject from "../game/GameObject.js";

/**
 * Represents a spatial chunk containing game objects
 */
type Chunk<T extends Record<string, GameObject[]>> = {
  [key in keyof T]?: T[key];
};

/**
 * Manages spatial partitioning for efficient collision detection and object processing
 */
class ChunkManager<T extends Record<string, GameObject[]>> {
  private chunks: Map<string, Chunk<T>> = new Map();

  /**
   * Creates a new SpatialChunkManager
   * @param objects Map of object lists to partition
   * @param chunkSize Size of each chunk (default: 20.0)
   * @param objectBuffer Extra buffer for object assignment (default: 5.0)
   */
  constructor(
    private objects: T,
    private readonly chunkSize: number = 20.0,
    private readonly objectBuffer: number = 5.0,
  ) {
    this.initializeSpatialPartitioning();
  }

  public getClosestChunk(position: Vec2): Chunk<T> | undefined {
    return this.chunks.get(this.getChunkCoordinates(position).toString());
  }

  public getNearbyObjects<K extends keyof T>(position: Vec2, type: K): T[K] {
    return (this.getClosestChunk(position)?.[type] ?? []) as T[K];
  }

  private getChunkCoordinates(position: Vec2): Vec2 {
    return position.clone().div(this.chunkSize).floor();
  }

  private initializeSpatialPartitioning() {
    for (const key in this.objects) {
      const objects: GameObject[] = this.objects[key] ?? [];
      const sortedObjects = [...objects].sort(GameObject.Comparator);
      for (const obj of sortedObjects) {
        for (const chunkCoordinate of this.getChunksForObject(obj)) {
          const chunkKey = chunkCoordinate.toString();
          if (!this.chunks.has(chunkKey)) this.chunks.set(chunkKey, {});
          const chunk = this.chunks.get(chunkKey)!;
          if (!(key in chunk)) chunk[key] = [] as unknown as T[typeof key];
          chunk[key]!.push(obj);
        }
      }
    }
  }

  /**
   * Get the chunks that an object might belong to based on its position and shape
   * Assumes all GameObjects have a rectangular shape
   * Adds an extra buffer to ensure objects near chunk boundaries are included
   */
  private getChunksForObject(obj: GameObject): Vec2[] {
    const result: Vec2[] = [];

    const aabb = AABB.fromGameObject(obj)
      .enlarge(this.objectBuffer)
      .div(this.chunkSize)
      .floor();

    // Add all chunks that overlap with the bounding box
    for (let x = aabb.lowerBound.x; x <= aabb.upperBound.x; x++) {
      for (let y = aabb.lowerBound.y; y <= aabb.upperBound.y; y++) {
        result.push(new Vec2(x, y));
      }
    }

    return result;
  }
}

export default ChunkManager;
